import { ChangeDetectionStrategy, Component, inject, Signal, input } from '@angular/core';
import { GameId } from 'src/app/shared/models/game/game-id';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ModalController } from '@ionic/angular/standalone';
import { safeModalDismiss } from 'src/app/core/services/modal/modal.utils';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { DuelTierDataWithMeta } from 'src/app/core/services/tiers/tiers.service';
import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { FreeMatchTierData } from 'src/app/core/api/player/schemas/tiers';
import { SoloTierData } from 'src/app/core/api/player/schemas/solo-tiers';
import { PaymentService } from 'src/app/features/payments/services/payment.service';
import { TournamentDataModel } from 'src/app/core/services/tournament/models/tournament-data-model';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { NavButtonComponent } from '../../../ui/buttons/nav-button/nav-button.component';
import { WagerTierListComponent } from '../components/wager-tier-list/wager-tier-list.component';
import { NgStyle, NgClass } from '@angular/common';
import {
  DuelPaymentEventData,
  FreeMatchPlayEventData,
  TournamentPaymentEventData,
} from 'src/app/core/services/tiers/models/payment-events';

@Component({
  selector: 'app-wager-drawer',
  templateUrl: './wager-drawer.component.html',
  styleUrls: ['./wager-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, NgClass, NavButtonComponent, WagerTierListComponent, ThemePipe],
})
export class WagerDrawerComponent {
  readonly gameId = input.required<GameId>();
  readonly allTiers = input.required<Signal<DuelTierDataWithMeta[]>>();
  readonly freeMatches = input.required<Signal<FreeMatchTierData[]>>();
  readonly currentTournamentData = input.required<Signal<TournamentDataModel | undefined>>();
  readonly soloTiers = input.required<Signal<SoloTierData[]>>();
  readonly waitingOpponentsStandard = input.required<Signal<WaitingOpponentsData | undefined>>();

  private modalController = inject(ModalController);
  private analyticsService = inject(AnalyticsService);
  readonly paymentInFlight = inject(PaymentService).paymentInFlight;

  themeService = inject(ThemeService);
  drawerConfig = this.themeService.payDrawerConfig();

  get drawerStyles(): [string, string][] {
    const key = 'drawer';
    return [
      ['background', key],
      ['border-radius', key],
    ];
  }

  get glowClass(): string {
    return this.themeService.payDrawerConfig().gradient === 'circle'
      ? 'glow-circle'
      : 'glow-linear';
  }

  async onPracticeClick(): Promise<void> {
    await safeModalDismiss(this.modalController, null, 'practiceClick');
    this.analyticsService.log(EventName.SelectTier, { tierId: 'practice' });
  }

  async onLocalClick(): Promise<void> {
    await safeModalDismiss(this.modalController, null, 'localClick');
  }

  async onWagerClick(data: DuelPaymentEventData): Promise<void> {
    await safeModalDismiss(this.modalController, data, 'wagerClick');
    this.analyticsService.log(EventName.SelectTier, { tierId: data.tierData.tierId });
  }

  async onFreeMatchClick(data: FreeMatchPlayEventData): Promise<void> {
    await safeModalDismiss(this.modalController, data, 'freeMatchClick');
    // Same event as a paid tap so tier-selection funnels stay whole; the extra properties are
    // what separates a free selection from a paid one downstream.
    this.analyticsService.log(EventName.SelectTier, {
      tierId: data.grant.tierId,
      freeMatch: true,
      grantId: data.grant.grantId,
    });
  }

  // The drawer closes before the Solo sheet opens; the controls behind it open the sheet on this
  // role, as they do for every other choice made here.
  async onSoloClick(): Promise<void> {
    await safeModalDismiss(this.modalController, null, 'soloClick');
  }

  async onTournamentPlayClick(data: TournamentPaymentEventData): Promise<void> {
    await safeModalDismiss(this.modalController, data, 'tournamentPlayClick');
    this.analyticsService.log(EventName.TournamentSelectTier, {
      tierId: data.tournamentData.tierId,
      tournamentId: data.tournamentData.tournamentId,
    });
  }

  async onCloseClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
  }
}
